import type { Message as DBMessage } from '@/generated/prisma/browser';
import type { AssistantModelMessage, ModelMessage, ToolModelMessage, UIMessage } from 'ai';
import type { ClassValue } from 'clsx';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const getGroupHeaderId = (groupName: string): string => {
  return `group-header-${groupName.replace(/\s+/g, '-').toLowerCase()}`;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type ApplicationError = {
  info: string;
  status: number;
} & Error;

export const fetcher = async (url: string) => {
  const res = await fetch(url);

  if (!res.ok) {
    const error = new Error('An error occurred while fetching the data.') as ApplicationError;

    error.info = await res.json();
    error.status = res.status;

    throw error;
  }

  return res.json();
};

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function stringifyPersistedToolError(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

export function convertToUIMessages(messages: Array<DBMessage>): Array<UIMessage> {
  // Walk tool messages first so assistant tool-call parts can be hydrated with
  // their persisted result. Without this, reloaded chats get stuck on
  // `input-available` and show an infinite "Running command..." loader.
  const toolResultsByCallId = new Map<string, { value: unknown; isError: boolean }>();
  for (const message of messages) {
    if (message.role !== 'tool') {
      continue;
    }
    const content =
      typeof message.content === 'string' ? JSON.parse(message.content) : message.content;
    if (!Array.isArray(content)) {
      continue;
    }
    for (const item of content) {
      if (item?.type !== 'tool-result') {
        continue;
      }
      const output = item.output ?? {};
      // AI SDK wraps structured results as { type, value }; support both wrapped and unwrapped shapes
      const isWrapped =
        typeof output === 'object' && output !== null && 'type' in output && 'value' in output;
      toolResultsByCallId.set(item.toolCallId, {
        value: isWrapped ? output.value : output,
        isError: isWrapped && (output.type === 'error-text' || output.type === 'error-json'),
      });
    }
  }

  return messages.reduce((chatMessages: Array<UIMessage>, message) => {
    if (message.role === 'tool') {
      return chatMessages;
    }

    let textContent = '';
    const toolInvocations: Array<any> = [];
    const reasoningParts: Array<{ type: 'reasoning'; text: string }> = [];

    const content =
      typeof message.content === 'string' ? JSON.parse(message.content) : message.content;

    if (typeof content === 'string') {
      textContent = content;
    } else if (Array.isArray(content)) {
      for (const item of content) {
        if (item.type === 'text') {
          textContent += item.text;
        } else if (item.type === 'reasoning') {
          reasoningParts.push({
            type: 'reasoning',
            text: typeof item.text === 'string' ? item.text : '',
          });
        } else if (item.type === 'tool-call') {
          toolInvocations.push({
            toolCallId: item.toolCallId,
            toolName: item.toolName,
            input: (item as any).input,
          });
        }
      }
    }

    // Create a UIMessage-compatible object
    // In v5, messages use parts array instead of content/toolInvocations
    const parts: any[] = [];
    // Reasoning parts go first (they appear above the response text in the UI)
    for (const reasoning of reasoningParts) {
      parts.push(reasoning);
    }
    if (textContent) {
      parts.push({ type: 'text', text: textContent });
    }
    // Merge persisted tool-result output onto each tool-call part so the UI
    // renders the final output instead of an infinite loader.
    toolInvocations.forEach((invocation: any) => {
      const result = toolResultsByCallId.get(invocation.toolCallId);
      parts.push({
        type: `tool-${invocation.toolName}`,
        toolCallId: invocation.toolCallId,
        input: invocation.input,
        ...(result === undefined
          ? { state: 'input-available' }
          : result.isError
            ? { state: 'output-error', errorText: stringifyPersistedToolError(result.value) }
            : { state: 'output-available', output: result.value }),
      });
    });

    chatMessages.push({
      id: message.id,
      role: message.role as UIMessage['role'],
      parts,
    } as UIMessage);

    return chatMessages;
  }, []);
}

export function sanitizeResponseMessages(
  messages: Array<ToolModelMessage | AssistantModelMessage>,
): Array<ToolModelMessage | AssistantModelMessage> {
  if (!Array.isArray(messages)) {
    console.warn('Messages is not an array:', messages);
    return [];
  }

  const toolResultIds: Array<string> = [];

  for (const message of messages) {
    if (message.role === 'tool') {
      for (const content of message.content) {
        if (content.type === 'tool-result') {
          toolResultIds.push(content.toolCallId);
        }
      }
    }
  }

  const messagesBySanitizedContent = messages.map((message) => {
    if (message.role !== 'assistant') {
      return message;
    }

    if (typeof message.content === 'string') {
      return message;
    }

    const sanitizedContent = message.content.filter((content) =>
      content.type === 'tool-call'
        ? toolResultIds.includes(content.toolCallId)
        : content.type === 'text'
          ? content.text.length > 0
          : true,
    );

    return {
      ...message,
      content: sanitizedContent,
    };
  });

  return messagesBySanitizedContent.filter((message) => message.content.length > 0);
}

export function getMostRecentUserMessage(messages: Array<ModelMessage>) {
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.at(-1);
}

// Returns ⌘ for Mac and Ctrl for the rest
export const metaChar = () => {
  if (typeof window === 'undefined') {
    return 'Ctrl';
  }
  return navigator.platform.includes('Mac') ? '⌘' : 'Ctrl';
};

export const metaShortcut = (key: string) => {
  const modifier = metaChar();
  return modifier === 'Ctrl' ? `${modifier}+${key}` : `${modifier}${key}`;
};

// Returns ⌥ for Mac and Alt for the rest
export const altChar = () => {
  if (typeof window === 'undefined') {
    return 'Alt';
  }
  return navigator.platform.includes('Mac') ? '⌥' : 'Alt';
};

export const altShortcut = (key: string) => {
  const modifier = altChar();
  return modifier === 'Alt' ? `${modifier}+${key}` : `${modifier}${key}`;
};
